import { useState, useRef, useCallback, useEffect } from 'react';
import { MediaRecorderManager } from '../utils/recordUtils';
import { RecordType } from '../types';

export const useMediaRecorder = (type: RecordType) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordedTime, setRecordedTime] = useState(0);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);

  const recorderManager = useRef<MediaRecorderManager>(new MediaRecorderManager());
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const MAX_RECORDING_TIME = 120; // Giới hạn 2 phút (120 giây)

  const startTimer = useCallback(() => {
    timerRef.current = setInterval(() => {
      setRecordedTime((prev) => prev + 1);
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const mediaStream = await recorderManager.current.startRecording(type);
      setStream(mediaStream);
      setIsRecording(true);
      setIsPaused(false);
      setRecordedTime(0);
      startTimer();
    } catch (error) {
      console.error('Failed to start recording:', error);
      alert('Không thể truy cập microphone/camera. Vui lòng kiểm tra quyền truy cập.');
    }
  }, [type, startTimer]);

  const pauseRecording = useCallback(() => {
    recorderManager.current.pauseRecording();
    setIsPaused(true);
    stopTimer();
  }, [stopTimer]);

  const resumeRecording = useCallback(() => {
    recorderManager.current.resumeRecording();
    setIsPaused(false);
    startTimer();
  }, [startTimer]);

  const stopRecording = useCallback(async () => {
    stopTimer();
    const blob = await recorderManager.current.stopRecording();
    setRecordedBlob(blob);
    setIsRecording(false);
    setIsPaused(false);
    setStream(null);
    return blob;
  }, [stopTimer]);

  const resetRecording = useCallback(() => {
    recorderManager.current.cleanup();
    setIsRecording(false);
    setIsPaused(false);
    setRecordedTime(0);
    setRecordedBlob(null);
    setStream(null);
    stopTimer();
  }, [stopTimer]);

  // Tự động dừng khi đạt giới hạn thời gian
  useEffect(() => {
    if (isRecording && !isPaused && recordedTime >= MAX_RECORDING_TIME) {
      stopRecording();
      alert('Đã đạt giới hạn thời gian ghi âm/video (2 phút)!');
    }
  }, [recordedTime, isRecording, isPaused, stopRecording, MAX_RECORDING_TIME]);

  useEffect(() => {
    return () => {
      recorderManager.current.cleanup();
      stopTimer();
    };
  }, [stopTimer]);

  return {
    isRecording,
    isPaused,
    recordedTime,
    stream,
    recordedBlob,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    resetRecording,
  };
};

